import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Save, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function AccountSettings() {
    const { user } = useAuth();
    const [currentPassword, setCurrentPassword] = useState("");
    const [newEmail, setNewEmail] = useState(user?.email || "");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!currentPassword) {
            setError("Please enter your current password.");
            return;
        }

        const emailChanged = newEmail.trim() && newEmail.trim().toLowerCase() !== user.email.toLowerCase();
        const passwordChanged = newPassword.length > 0;

        if (!emailChanged && !passwordChanged) {
            setError("Change at least your email or your password.");
            return;
        }

        if (passwordChanged) {
            if (newPassword.length < 6) {
                setError("New password must be at least 6 characters.");
                return;
            }
            if (newPassword !== confirmPassword) {
                setError("New password and confirmation don't match.");
                return;
            }
        }

        const payload = { current_password: currentPassword };
        if (emailChanged) payload.new_email = newEmail.trim().toLowerCase();
        if (passwordChanged) payload.new_password = newPassword;

        setSaving(true);
        try {
            const { data } = await api.post("/auth/change-credentials", payload);
            // Update stored token and user
            if (data?.token) localStorage.setItem("wm_token", data.token);
            toast.success("Credentials updated. Use them next time you sign in.");
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
            // Soft reload to refresh auth context user
            setTimeout(() => window.location.reload(), 600);
        } catch (e2) {
            const msg = formatApiErrorDetail(e2.response?.data?.detail) || e2.message;
            setError(msg);
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="account-settings" className="max-w-2xl">
            <div className="border-b border-[#E5DED5] pb-6 mb-8">
                <div className="flex items-start gap-3">
                    <ShieldCheck className="w-5 h-5 mt-1 text-[#A0522D]" />
                    <div>
                        <p className="overline">Account & security</p>
                        <p className="font-serif text-2xl mt-1">Change your login email and password.</p>
                        <p className="text-sm text-[#5C5853] mt-2">
                            You'll need your current password to confirm. After saving, your new credentials are active immediately — keep them somewhere safe.
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={onSubmit} className="space-y-7" data-testid="account-settings-form">
                <div>
                    <label className="overline">Current password</label>
                    <input
                        data-testid="account-current-password"
                        type="password"
                        className="input-underline"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="••••••••"
                        autoComplete="current-password"
                        required
                    />
                </div>

                <div className="border-t border-[#E5DED5] pt-7 space-y-7">
                    <div>
                        <label className="overline">Login email</label>
                        <input
                            data-testid="account-new-email"
                            type="email"
                            className="input-underline"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            placeholder="you@email.com"
                            autoComplete="email"
                        />
                        <p className="text-xs text-[#5C5853] mt-2">
                            This is the email you sign in with — separate from your public contact email.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                            <label className="overline">New password</label>
                            <input
                                data-testid="account-new-password"
                                type="password"
                                className="input-underline"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="At least 6 characters"
                                autoComplete="new-password"
                            />
                        </div>
                        <div>
                            <label className="overline">Confirm new password</label>
                            <input
                                data-testid="account-confirm-password"
                                type="password"
                                className="input-underline"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Re-enter"
                                autoComplete="new-password"
                            />
                        </div>
                    </div>
                    <p className="text-xs text-[#5C5853]">
                        Leave password fields empty if you only want to change your email.
                    </p>
                </div>

                {error && (
                    <p data-testid="account-settings-error" className="text-sm text-[#7A2E2E]">{error}</p>
                )}

                <div className="border-t border-[#E5DED5] pt-6">
                    <button
                        type="submit"
                        data-testid="account-settings-save"
                        disabled={saving}
                        className="btn-editorial primary disabled:opacity-60"
                    >
                        {saving ? "Saving…" : (<>Save credentials <Save className="w-4 h-4" /></>)}
                    </button>
                </div>
            </form>
        </div>
    );
}
